import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Sparkles, TrendingUp, ArrowUp, ArrowDown, CheckCircle2, Loader2, Info } from "lucide-react";

/**
 * AI Recommended Pricing card for the Estimator page.
 * Combines USAspending market benchmark + peer wins into a single baseline total
 * so the operator can instantly see if their live grand_total is HIGH or LOW.
 * Free — no credit spend on load.
 */
export default function RecommendedPricingCard({ noticeId, liveTotal }) {
  const [rec, setRec] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!noticeId) return;
    let ok = true;
    setLoading(true);
    api.get(`/ai/estimate/${noticeId}/recommended-total`)
      .then((r) => { if (ok) setRec(r.data); })
      .catch(() => { if (ok) setRec({ available: false, message: "Could not compute a recommendation right now." }); })
      .finally(() => { if (ok) setLoading(false); });
    return () => { ok = false; };
  }, [noticeId]);

  if (loading) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-5 flex items-center gap-3 text-sm text-slate-500" data-testid="rec-pricing-loading">
        <Loader2 className="w-4 h-4 animate-spin" />
        Computing AI recommended pricing…
      </div>
    );
  }
  if (!rec || rec.available === false) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-5 flex items-start gap-3 text-sm text-slate-600" data-testid="rec-pricing-empty">
        <Info className="w-4 h-4 text-slate-400 mt-0.5" />
        <div>
          <div className="font-display font-bold text-slate-800 mb-1">AI Recommended Pricing</div>
          <div>{rec?.message || "Not enough matching market or peer data to compute a recommendation for this opportunity yet."}</div>
        </div>
      </div>
    );
  }

  const total = Number(liveTotal || 0);
  const inBand = total >= rec.low && total <= rec.high;
  const above  = total > rec.high;
  const below  = total > 0 && total < rec.low;
  const verdict = total === 0 ? "empty" : inBand ? "ok" : above ? "high" : below ? "low" : "empty";
  const delta = total && rec.baseline ? Math.round(((total - rec.baseline) / rec.baseline) * 100) : 0;

  const verdictClasses = {
    ok:    "bg-emerald-50 border-emerald-200 text-emerald-800",
    high:  "bg-rose-50 border-rose-200 text-rose-800",
    low:   "bg-amber-50 border-amber-200 text-amber-800",
    empty: "bg-slate-50 border-slate-200 text-slate-600",
  }[verdict];
  const VerdictIcon = verdict === "ok" ? CheckCircle2 : verdict === "high" ? ArrowUp : verdict === "low" ? ArrowDown : Info;
  const verdictText = {
    ok:    `Your total sits inside the AI recommended band — ${delta > 0 ? "+" : ""}${delta}% vs baseline.`,
    high:  `Your total is above the recommended high — ${delta > 0 ? "+" : ""}${delta}% vs baseline. Consider trimming line items or documenting the premium.`,
    low:   `Your total is below the recommended low — ${delta}% vs baseline. Double-check you didn't miss scope.`,
    empty: `Fill in your line-item unit prices to compare against the recommendation.`,
  }[verdict];

  const fmt = (v) => `$${Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

  return (
    <div className="rounded-2xl border border-fuchsia-200 bg-gradient-to-br from-fuchsia-50 via-white to-violet-50 p-5" data-testid="rec-pricing-card">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-fuchsia-500 to-violet-500 text-white flex items-center justify-center">
          <Sparkles className="w-5 h-5" />
        </div>
        <div>
          <div className="text-[10px] font-mono font-bold tracking-widest text-fuchsia-700">// AI RECOMMENDED PRICING</div>
          <div className="font-display font-bold text-lg text-slate-900">Bid-specific market baseline</div>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2 mb-4">
        <div className="rounded-xl bg-white border border-slate-200 p-3 text-center">
          <div className="text-[9px] font-mono font-bold tracking-widest text-slate-400 mb-1">RECOMMENDED LOW</div>
          <div className="font-mono font-bold text-slate-700" data-testid="rec-pricing-low">{fmt(rec.low)}</div>
        </div>
        <div className="rounded-xl bg-gradient-to-br from-fuchsia-100 to-violet-100 border border-fuchsia-300 p-3 text-center">
          <div className="text-[9px] font-mono font-bold tracking-widest text-fuchsia-700 mb-1">BASELINE</div>
          <div className="font-display font-black text-xl text-fuchsia-800" data-testid="rec-pricing-baseline">{fmt(rec.baseline)}</div>
        </div>
        <div className="rounded-xl bg-white border border-slate-200 p-3 text-center">
          <div className="text-[9px] font-mono font-bold tracking-widest text-slate-400 mb-1">RECOMMENDED HIGH</div>
          <div className="font-mono font-bold text-slate-700" data-testid="rec-pricing-high">{fmt(rec.high)}</div>
        </div>
      </div>

      <div className={`rounded-lg border p-3 flex items-start gap-2 mb-3 ${verdictClasses}`} data-testid={`rec-pricing-verdict-${verdict}`}>
        <VerdictIcon className="w-4 h-4 shrink-0 mt-0.5" />
        <div className="text-xs leading-relaxed">
          <div>Your live total: <b>{fmt(total)}</b></div>
          <div className="mt-1">{verdictText}</div>
        </div>
      </div>

      <div className="text-[11px] text-slate-500 leading-relaxed">
        <b className="text-slate-700">Basis:</b> {rec.basis}
        {rec.strategy_used && <> · matched via {rec.strategy_used}</>}
      </div>
      <div className="mt-3 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2 flex items-start gap-2" data-testid="rec-pricing-disclaimer">
        <Info className="w-3.5 h-3.5 shrink-0 mt-0.5" />
        <span><b>Disclaimer:</b> Baseline is generated from public market data and peer wins, not from your actual cost basis. Always review against your walk-through, subs, and RFP requirements before submitting.</span>
      </div>
    </div>
  );
}
